import { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { eur, num } from "@/lib/format";
import { useTheme } from "@/lib/theme";

const RANGES = [
  { key: "5d", label: "5T" },
  { key: "1mo", label: "1M" },
  { key: "6mo", label: "6M" },
  { key: "1y", label: "1J" },
  { key: "5y", label: "5J" },
];

const fmtDate = (iso, range) =>
  new Date(iso).toLocaleDateString("de-DE", range === "5d" ? { weekday: "short", hour: "2-digit" } : range === "5y" ? { month: "short", year: "2-digit" } : { day: "2-digit", month: "short" });

const ChartTip = ({ active, payload, range, unit, digits }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-xl">
      <div className="text-slate-500">{fmtDate(payload[0].payload.t, range === "5d" ? "5d" : "1mo")}</div>
      <div className="num text-sm font-semibold text-orange-700">{eur(payload[0].value, digits)}{unit.slice(1)}</div>
    </div>
  ) : null;

export default function PriceChart({ factor = 1, height = 300, metal = "kupfer", digits = 3, unit = "€/kg" }) {
  const api = useApi();
  const dark = useTheme() === "dark";
  const [range, setRange] = useState("6mo");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    api
      .get("/prices/history", { params: { range, metal } })
      .then((r) => setData(r.data.map((p) => ({ t: p.t, v: +(p.eur_kg * factor).toFixed(4) }))))
      .catch(() => setError(true));
  }, [api, range, factor, metal]);

  const first = data?.[0]?.v;
  const last = data?.[data.length - 1]?.v;
  const change = first ? ((last / first - 1) * 100) : null;

  return (
    <div data-testid="chart-copper-history">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          Veränderung im Zeitraum:{" "}
          <span className={`num font-semibold ${change >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="chart-range-change">
            {change == null ? "–" : `${change >= 0 ? "+" : ""}${num(change)} %`}
          </span>
        </div>
        <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`btn-timeframe-${r.key}`}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${range === r.key ? "bg-orange-600 text-white" : "text-slate-500 hover:text-slate-900"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ height }}>
        {error ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">Kurshistorie derzeit nicht verfügbar</div>
        ) : !data ? (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="cu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EA580C" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#EA580C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={dark ? "rgba(255,255,255,0.06)" : "#EDE7E0"} vertical={false} />
              <XAxis dataKey="t" tickFormatter={(t) => fmtDate(t, range)} tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={40} />
              <YAxis domain={["auto", "auto"]} tickFormatter={(v) => num(v, digits === 0 ? 0 : 2)} tick={{ fill: "#64748B", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={48} />
              <Tooltip content={<ChartTip range={range} unit={unit} digits={digits} />} />
              <Area type="monotone" dataKey="v" stroke="#F97316" strokeWidth={2} fill="url(#cu)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
